import React from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  Modal,
  toast,
} from '@workwhile/ui'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { editRuleSchema, EditRuleSchema } from '../schemas/editRule'
import { useEditRuleFlowContextValue } from '../context/EditRuleFlowProvider'
import { useRuleDetailContextValue } from '../../../context/RuleDetailProvider'
import { useRuleDetailWorkflowsContextValue } from '../../../context/RuleDetailWorkflowsProvider'

export const EditRuleFlowMain = () => {
  const {
    state: { id, effectiveEnd, reference },
  } = useRuleDetailContextValue()

  const {
    editRuleFlow: { isOpen, close },
  } = useRuleDetailWorkflowsContextValue()

  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit },
  } = useEditRuleFlowContextValue()

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<EditRuleSchema>({
    resolver: zodResolver(editRuleSchema),
    defaultValues: {
      effectiveEnd: effectiveEnd ?? '',
      reference: reference ?? '',
    },
  })

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        ruleId: id,
        effectiveEnd: data.effectiveEnd,
        reference: data.reference,
      },
      {
        onSuccess: () => {
          toast.success('Rule updated successfully', { position: 'top-center' })
          close()
        },
      }
    )
  })

  const handleClose = () => {
    reset()
    close()
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Save
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Edit rule
        </Heading>
        <Box mt={3}>
          <Field label="Effective end" error={errors.effectiveEnd?.message}>
            <Input {...register('effectiveEnd')} type="date" />
          </Field>
        </Box>
        <Box mt={3}>
          <Field label="Reference" error={errors.reference?.message}>
            <Input {...register('reference')} />
          </Field>
        </Box>
        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </Modal>
  )
}
